<template>
  <v-row class="my-investments">
    <v-col class="account-details">
      <v-row class="header flex-column">
        <v-col>
          <h3 class="text-h2">{{ $t('L_account_details') }}</h3>
        </v-col>
        <v-col class="text-h4 font-weight-regular">
          {{ $t('L_account_snapshot') }}
        </v-col>
        <v-col cols="12" class="mt-4 mb-4">
          <span class="mr-2">{{ $t('L_some_accounts_may_not_be_displayed') }}</span>
          <div v-if="$vuetify.display.xs">&nbsp;</div>
          <span
            @click="openAvailableProducts()"
            class="blueBoldedFont"
            :class="$vuetify.display.xs ? 'spacingLearnMoreXsOnly' : ''"
            >{{ $t('L_learn_more2') }}</span
          >
        </v-col>
        <v-col cols="12" class="mb-2" v-if="allowLinkMinorAccounts">
          <a
            href="javascript:void(0)"
            @click="navigateToLinkMinorsAccountPage"
            class="link-blue-no-dec"
          >
            <v-icon
              @click="navigateToLinkMinorsAccountPage"
              size="20"
              color="primary"
              :icon="mdiLink"
            />
            {{ $t('L_link_a_minors_acct') }}
          </a>
          <v-icon
            color="primary"
            class="cursor-pointer ml-1"
            size="20"
            @click.stop="showLinkMinorAcctTooltip = !showLinkMinorAcctTooltip"
            @blur="showLinkMinorAcctTooltip = !showLinkMinorAcctTooltip"
            @focus="showLinkMinorAcctTooltip = !showLinkMinorAcctTooltip"
            :icon="mdiInformation"
          />
          <v-tooltip
            v-model="showLinkMinorAcctTooltip"
            content-class="text-center"
            location="right"
            color="primary"
            :max-width="$vuetify.display.mdAndUp ? '194' : '162'"
          >
            <template v-slot:activator="{ props }">
              <v-icon size="small" v-bind="props" />
            </template>
            <span role="tooltip" aria-live="polite" id="minor_link_tooltip">
              {{ $t('L_minor_account_tooltip') }}
            </span>
          </v-tooltip>
        </v-col>
      </v-row>

      <div cols="12" v-for="(group, index) in investments?.groups" :key="group.name">
        <ResponsiveTable
          class="responsive-table"
          :display-header="displayHeaders(index)"
          :headers="headers"
          :data="accounts(group.accounts || [], group.name || '')"
          @clicked-item="openDisclaimer"
          @linked-item="openAccountNumber"
        />
        <div v-if="group.name">
          <v-row v-if="isMobile" class="subfooter">
            <v-col cols="7" sm="10" class="text-left font-weight-bold">
              {{ $t('L_investment_account_subtotal', { name: group.name }) }}
            </v-col>
            <v-col cols="5" sm="2" class="text-right font-weight-bold">
              {{ $n(group.total, 'currency', 'en-US') }}
            </v-col>
          </v-row>
          <v-row v-else text-xs-right class="subfooter">
            <v-col cols="7" sm="10" class="font-weight-bold font-size--14 text-right">
              {{ $t('L_investment_account_subtotal', { name: group.name }) }}
            </v-col>
            <v-col cols="5" sm="2" class="font-weight-bold font-size--14 text-right">
              {{ $n(group.total, 'currency', 'en-US') }}
            </v-col>
          </v-row>

          <v-row v-if="isCanadian" class="subfooter text-right">
            <v-col cols="7" sm="10" class="font-size--14 mt-0 pt-0">
              {{ $t(investmentAccountName(group.offeredBy || '')) }}
            </v-col>
            <v-col cols="5" sm="2" />
          </v-row>
        </div>
      </div>
      <!-- Total Value -->
      <v-row justify-center class="footer">
        <v-row justify-center class="total font-weight-bold pt-3 pb-3 pr-1">
          <v-spacer />
          <v-col cols="6" sm="3" md="2" lg="2" class="label text-left d-flex align-center">
            {{ $t('L_total_value') }}
          </v-col>
          <v-col cols="6" sm="3" md="2" lg="2" class="text-right">
            <span v-if="investments?.summary?.totalValue">{{
              $n(investments.summary.totalValue, 'currency', 'en-US')
            }}</span>
            <span v-else>$0</span>
          </v-col>
        </v-row>
      </v-row>

      <!-- Custodial Accounts -->
      <v-row column v-if="displayMinorAccounts">
        <v-col class="account px-3 mt-5 mb-2 font-weight-bold d-flex">
          <v-icon
            aria-hidden="true"
            class="v-icon v-icon--link material-icons theme--light gray--text mr-2"
            style="color: #02579e"
            :size="18"
            :icon="mdiChartBar"
          />
          <h5 class="group font-size--14">{{ $t('L_linked_custodian_accounts') }}</h5>
        </v-col>
        <LinkedCustodianAccounts
          class="responsive-table"
          :display-header="false"
          :headers="headers"
          :data="custodians"
          @company-name-clicked="openDisclaimer"
          @account-number-clicked="openAccountNumber"
        />
      </v-row>
      <!-- Footer -->
      <v-row justify-center class="footer">
        <div>
          <v-row justify-center pt-4>
            <v-col cols="12" sm="12" md="12" lg="12" class="disclosure body-2 text-xs-left pl-0">
              <!-- Balances may not be current or reflect all of your accounts opened through PFSL and PLICC.   -->
              <!-- For up to date and complete information, refer to your account statements and confirmations.  -->
              <!-- The information above must be read with the disclosures contained  -->
              <span v-if="isCanadian"
                >{{ $t('L_important_disclosures_ca') }}
                <span
                  class="cursor-pointer font-weight-semibold"
                  color="primary"
                  @click="openDisclosure()"
                >
                  {{ $t('L_here') }}
                </span>
              </span>
              <div v-else>
                <p>
                  <!-- The information contained on this website is designed solely for your individual use and is for information purposes only. -->
                  <!-- For the most accurate information about your account(s), please review your official account statements and confirmations. See -->
                  <span
                    >{{ $t('L_important_disclosures_us') }}
                    <span
                      class="cursor-pointer font-weight-semibold"
                      color="primary"
                      @click="openDisclosure()"
                    >
                      {{ $t('L_important_disclosures') }}
                    </span>
                  </span>
                </p>
                <p v-if="summary?.totalValue > 0" class="mt-4">
                  <span>{{ $t('L_for_company_contact_information') }}</span>
                  <a v-if="!isCanadian" @click="helpAction" class="link-blue-no-dec font-size--16"
                    >{{ $t('L_click_here') }}
                  </a>
                </p>
              </div>
            </v-col>
            <v-spacer />
          </v-row>
        </div>
        <Statements v-if="isCanadian && hasInvestments" />
      </v-row>

      <v-dialog
        v-model="dialog"
        max-width="914px"
        :fullscreen="$vuetify.display.xs"
        :min-height="$vuetify.display.xs ? '92vh' : 'fit-content'"
        z-index="10000"
      >
        <v-card :class="$vuetify.display.xs ? 'px-4' : 'px-10'">
          <v-card-title class="text-center">
            <v-col cols="12">
              <h1>{{ $t('L_bny_disclaimer_title') }}</h1>
            </v-col>
          </v-card-title>
          <v-card-text :class="{ 'pb-2': $vuetify.display.xs }">
            <div class="font-weight-semibold">
              {{ $t('L_bny_disclaimer_label') }}
            </div>
            <div>{{ $t('L_bny_disclaimer') }}</div>
          </v-card-text>
          <v-card-actions class="row justify-center" :class="{ 'pb-10 pt-0': $vuetify.display.xs }">
            <v-col cols="6" class="text-right">
              <OnexButton variant="outlined" color="outlined" width="200" @click="dialog = false">
                {{ $t('L_bny_disclaimer_cancel') }}
              </OnexButton>
            </v-col>
            <v-col cols="6" class="text-left">
              <OnexButton width="200" @click="manageAccount">
                {{ $t('L_bny_disclaimer_agree') }}
              </OnexButton>
            </v-col>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-col>
    <EmbeddedViewer
      :title="document.title"
      :loading="document.loading"
      :content-type="document.type"
      :content-url="document.content"
      @close="document.content = undefined"
    />
  </v-row>
  <div style="height: 20px"></div>
  <v-row class="mb-8">
    <Representative v-if="hasAgents" :agents="agents" />
    <MessageDialog
      v-if="dialogNumber?.display"
      :show-dialog="dialogNumber?.display"
      :has-button-two="false"
      :is-persistent="true"
      :type="dialogNumber?.type"
      :title="$t('L_account_number')"
      :message="dialogNumber?.message"
      message-align="center"
      :button-one-label="$t('L_ok')"
      @buttonOneAction="dialogNumber = undefined"
    />
  </v-row>
</template>

<script lang="ts">
import { defineComponent, type ComponentOptions } from 'vue';
import { currency, percentage } from '@/mixins/numbers';
import MessageDialog from '@/components/MessageDialog.vue';
import ResponsiveTable from '@/components/ResponsiveTable/ResponsiveTable.vue';
import Representative from '@/components/Representative.vue';
import EmbeddedViewer from '@/components/EmbeddedViewer.vue';
import { openInNewWindow } from '@/mixins/openNewWindow';
import Statements from './Statements.vue';
import LinkedCustodianAccounts from './LinkedCustodianAccounts.vue';
import { mapActions, mapState, mapStores } from 'pinia';
import { useGlobalStore } from '@/stores/global/global.store';
import { useCustomerStore } from '@/stores/customer/customer.store';
import { COUNTRIES, FEATURE_FLAGS } from '@/shared/models/constants.models';
import { mdiChartBar, mdiInformation, mdiLink, mdiLaunch, mdiEye } from '@mdi/js';
import { OnexButton } from '@primerica/onex-ui-lib';
import { RouteNames } from '@/router/routeNames';
import type {
  DialogNumber,
  DisplayedInvestmentsGroupAccount,
} from '@/modules/investments/invetments.model.ts';
import type { IGlobalStoreAgent } from '@/stores/global/globalStore.model.ts';
import type { TableRowData } from '@/shared/models/components.model.ts';
import type { ICustomerStoreInvestmentsGroupAccount } from '@/stores/customer/customerStore.model.ts';

interface IAcctNumber {
  accountNumber: {
    id: string | number;
  };
}

const componentOptions: ComponentOptions = {
  name: 'MyInvestments',
  mixins: [currency, percentage, openInNewWindow],
  components: {
    Statements,
    Representative,
    ResponsiveTable,
    EmbeddedViewer,
    MessageDialog,
    LinkedCustodianAccounts,
    OnexButton,
  },
  data() {
    return {
      mdiInformation,
      mdiChartBar,
      mdiLink,
      mdiLaunch,
      mdiEye,
      document: {
        title: '',
        content: '' as string | undefined,
        type: '',
        loading: false as boolean | undefined,
      },
      dialog: false,
      account: {} as DisplayedInvestmentsGroupAccount | null,
      ssoRaUrl: '',
      bffSamlTokenVal: '',
      persistent: true,
      dialogNumber: {
        display: false,
        type: 'info',
        message: '',
      } as DialogNumber,
      showLinkMinorAcctTooltip: false,
    };
  },
  computed: {
    ...mapStores(useGlobalStore),
    ...mapState(useGlobalStore, ['containsFeature']),
    ...mapState(useCustomerStore, [
      'customerProfile',
      'investments',
      'loading',
      'investmentsSortedByDate',
    ]),
    ...mapState(useGlobalStore, {
      locale: 'getLocale',
    }),
    ...mapState(useGlobalStore, ['agents', 'getAgent']),
    isNotProd() {
      return window.appConfig.env.valueOf() !== 'prod';
    },
    hasInvestments() {
      return this.investments.summary.totalValue > 0;
    },
    hasStatements() {
      return this.investments?.statements?.length;
    },
    isCanadian() {
      return this.customerProfile.country === COUNTRIES.CANADA;
    },
    hasAgents() {
      return this.agents?.length > 0;
    },
    isMobile() {
      return this.$vuetify.display.xs;
    },
    agents() {
      const reps: IGlobalStoreAgent[] = [];
      if (this.investmentsSortedByDate) {
        let index = 0;
        this.investmentsSortedByDate.forEach((acct: ICustomerStoreInvestmentsGroupAccount) => {
          const agent = Object.assign({}, this.getAgent(acct.agentId));
          if (agent.agentId) {
            agent.account = `${acct.productType} ${acct.accountNumber}`;
            agent.index = index;
            reps.push(agent);
            index += 1;
          }
        });
      }
      return reps;
    },
    summary() {
      return this.investments?.summary;
    },
    custodians() {
      return this.investments?.custodians;
    },
    headers() {
      if (this.isCanadian) {
        return [
          {
            title: this.$t('L_account'),
            value: 'accountNumber',
          },
          {
            title: '',
            value: '',
          },
          {
            title: this.$t('L_account_ending_value'),
            value: 'endingValue',
          },
        ];
      } else if (this.isMobile) {
        return [
          {
            title: this.$t('L_account'),
            value: 'accountNumber',
          },
          {
            title: '',
            value: 'companyName',
          },
          {
            title: this.$t('L_account_ending_value'),
            value: 'endingValue',
          },
        ];
      }
      return [
        {
          title: this.$t('L_account'),
          value: 'accountNumber',
        },
        {
          title: this.$t('L_account_company'),
          value: 'companyName',
        },
        {
          title: this.$t('L_account_ending_value'),
          value: 'endingValue',
        },
      ];
    },
    allowLinkMinorAccounts() {
      return this.containsFeature(FEATURE_FLAGS.INVESTMENT_CUSTODIAN);
    },
    displayMinorAccounts() {
      return this.investments?.custodians?.length > 0;
    },
    allowViewAccountNumber() {
      return this.containsFeature(FEATURE_FLAGS.VIEW_ACCOUNT_NUMBER);
    },
  },
  mounted() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  },
  methods: {
    ...mapActions(useCustomerStore, ['getAccountNumber']),
    async navigateToLinkMinorsAccountPage() {
      // track in cp activity log
      await this.globalStore.postAuditLog({ actionId: 'MinorAccountLink_StartAction' });
      this.$router?.push({ name: RouteNames.MINOR_ACCOUNT_LINK_PROCESS });
    },
    timeout(ms: number) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },
    async openAvailableProducts() {
      let respUrl = '';
      const availableProducts = window.appConfig.investments.available_products;
      if (this.locale === 'fr_CA') {
        respUrl = typeof availableProducts.fr_CA === 'string' ? availableProducts.fr_CA : '';
      } else if (this.locale === 'en_US') {
        respUrl = typeof availableProducts.en_US === 'string' ? availableProducts.en_US : '';
      } else if (this.locale === 'en_CA') {
        respUrl = typeof availableProducts.en_CA === 'string' ? availableProducts.en_CA : '';
      }
      await this.openInNewWindow({ url: respUrl, target: '_blank' });
    },
    async helpAction() {
      const embedUrl =
        'https://assets.contentstack.io/v3/assets/blt1dedeb682e8df2a2/blt968817b7969bbd3b/RightAnswers_Investment_US_Custodian_Contact_List.pdf';
      await this.openInNewWindow({ url: embedUrl, target: 'RADashboardWinName' });
    },
    investmentAccountName(offeredBy: string) {
      return `L_investment_account_subtotal_${offeredBy}`;
    },
    displayHeaders(index: number) {
      if (index === 0) {
        return true;
      }
      return false;
    },
    accounts(arrs: ICustomerStoreInvestmentsGroupAccount[], groupName: string) {
      const baseIconHtml = `<i [ID] class="v-icon notranslate v-theme--primerica mr-2" aria-hidden="true" style="font-size: 20px; height: 20px; width: 20px; color: rgb(2, 87, 158); caret-color: rgb(2, 87, 158);"><svg class="v-icon__svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" role="img" aria-hidden="true"><path d="[ICON_PATH]"></path></svg></i>`;
      const eyeIcon = baseIconHtml.replace('[ICON_PATH]', this.mdiEye).replace('[ID]', 'id="link"');
      const launchIcon = baseIconHtml
        .replace('[ICON_PATH]', this.mdiLaunch)
        .replace('[ID]', 'id="click"');
      const graphIcon = baseIconHtml.replace('[ICON_PATH]', this.mdiChartBar).replace('[ID]', '');
      const accounts = [] as DisplayedInvestmentsGroupAccount[];
      if (groupName && !this.isCanadian) {
        const account = {
          accountNumber: groupName,
          isGroup: true,
        } as unknown as DisplayedInvestmentsGroupAccount;
        accounts.push(account);
      }
      arrs.forEach((arr) => {
        accounts.push(arr);
      });
      let index = 0;
      return accounts.map((accountDetails: DisplayedInvestmentsGroupAccount) => {
        const account: TableRowData = {};
        index += 1;
        Object.keys(accountDetails).forEach((p) => {
          const prop = p as keyof DisplayedInvestmentsGroupAccount;
          if (prop === 'accountNumber') {
            if (accountDetails.isGroup) {
              let markup = '<div class="account">';
              markup += `${graphIcon}&nbsp;<span class="group">${accountDetails.accountNumber}</span></div>`;
              account[prop] = {
                markup,
                clickable: false,
              };
              account.background = 'white';
            } else {
              let markup = '<div class="account"><div class="link">';
              if (this.isCanadian) {
                markup += `<span class="name">${accountDetails.productType}</span>`;
                if (index % 2 === 0) {
                  // even
                  account.background = 'white';
                } else {
                  account.background = 'primarylight2';
                }
              } else if (!this.isCanadian) {
                if (index % 2 === 0) {
                  // even
                  account.background = 'primarylight2';
                } else {
                  account.background = 'white';
                }
              }
              const linkable = this.allowViewAccountNumber;
              markup += `${accountDetails.accountNumber} `;
              markup += this.allowViewAccountNumber ? `${eyeIcon}</div>` : '</div>';
              markup += `<div class="type">${accountDetails.accountType}</div>`;
              if (this.isMobile) {
                markup += `<div class="link${accountDetails.companyLink ? ' bny' : ''}">`;
                markup += `<div id="click" class="type">${accountDetails.companyName}</div>`;
                markup += accountDetails.companyLink ? `${launchIcon}</div>` : '</div>';
                account[prop] = {
                  markup,
                  clickable: !!accountDetails.companyLink,
                  linkable,
                  id: accountDetails.id as string,
                };
              } else {
                account[prop] = {
                  markup,
                  clickable: false,
                  linkable,
                  id: accountDetails.id as string,
                };
              }
            }
          } else if (prop === 'companyName') {
            if (!this.isMobile) {
              let markup = '<div class="account">';
              markup += `<div class="link${accountDetails.companyLink ? ' bny' : ''}">`;
              markup += `<span id="click" class="type">${accountDetails.companyName}</span>`;
              markup += accountDetails.companyLink ? `${launchIcon}</div>` : '</div>';
              markup += '</div>';
              account[prop] = {
                markup,
                clickable: !!accountDetails.companyLink,
              };
            } else {
              account[prop] = { text: ' ', class: '' };
            }
          } else if (prop === 'endingValue') {
            let markup = `<div class="ending">`;
            const asOf = this.$t('L_as_of');
            markup += `<div class="value">${this.currency(accountDetails[prop])}</div>`;
            markup += `<div class="date">${asOf} ${accountDetails.asOfDate}</div></div>`;
            account[prop] = { markup };
          } else {
            let text;
            let klass;
            if (prop === 'inceptionROR') {
              klass = 'ror';
              text = this.percentage(accountDetails[prop]);
            } else {
              klass =
                typeof accountDetails[prop] === 'number' && accountDetails[prop] < 0
                  ? 'negative'
                  : '';
              text =
                typeof accountDetails[prop] === 'number'
                  ? this.currency(accountDetails[prop])
                  : (accountDetails[prop] as string);
            }
            account[prop] = { text, class: klass };
          }
        });
        return account;
      });
    },
    openDisclaimer(account: DisplayedInvestmentsGroupAccount) {
      this.account = account;
      // Skip disclaimer and open the window without if isBny = true
      if (
        (typeof account.isBNY === 'boolean' && account.isBNY) ||
        (typeof account.isBNY !== 'boolean' && account.isBNY?.text)
      ) {
        this.dialog = false;
        this.manageAccount();
      } else {
        this.dialog = true;
      }
    },
    async openAccountNumber(account: IAcctNumber) {
      const accountNumber = await this.getAccountNumber(account.accountNumber.id);
      if (accountNumber) {
        this.dialogNumber = {
          type: 'info',
          message: accountNumber,
          display: true,
        };
      } else {
        this.dialogNumber = {
          type: 'error',
          message: this.$t('L_view_number_error'),
          display: true,
        };
      }
    },
    async manageAccount() {
      if (
        this.account.accountNumber &&
        (this.account.companyLink || this.account?.companyLink?.text)
      ) {
        const link = this.account.companyLink.text
          ? this.account.companyLink.text
          : this.account.companyLink;
        this.account = {};
        this.dialog = false;
        await this.openInNewWindow({ url: link, target: '_blank' });
      }
    },
    openDisclosure() {
      this.document.type = 'application/pdf';
      this.document.title = this.$t('L_important_disclosures');
      if (this.customerProfile.country.toLowerCase() === 'ca') {
        this.document.content = `${window.appConfig.content_stack}${window.appConfig.investments.important_disclosures.ca}`;
      } else {
        this.document.content = `${window.appConfig.content_stack}${window.appConfig.investments.important_disclosures.us}`;
      }
    },
  },
};
export default defineComponent(componentOptions);
</script>

<style lang="scss">
.spacingLearnMoreXsOnly {
  padding-left: 32px;
}

.blueBoldedFont {
  color: #02579e;
  font-weight: bold;
  cursor: pointer;
}

.buttonFormat {
  width: 200px;
  height: 44px;
  font-size: 18px;
  font-weight: 600;
}

.buttonFormat_mobile {
  width: 180px;
  height: 44px;
  font-size: 14px;
  font-weight: 600;
  margin-left: 0 !important;
}

span.number {
  cursor: pointer;
}

.my-investments {
  background: transparent !important;
  .total {
    background-color: #e2f0fe;
  }

  .disclaimer_heading {
    font-size: 20px;
  }

  .disclosure {
    padding-left: 25px;
    padding-right: 25px;
    padding-bottom: 10px;
  }

  .account-details {
    background-color: #fff;
  }

  .header,
  .title {
    padding: 15px;
  }

  .header {
    border-bottom: 1px solid #ccc;

    h3 {
      font-size: 24px;
      font-weight: 400;
    }

    .subtitle {
      color: #4a4a4a;
      padding: 0;
      font-size: 20px;
    }
  }

  .responsive-table {
    padding: 5px 15px;

    .v-table,
    tr {
      border: none !important;
    }

    td,
    th {
      color: #333;
      font-size: 14px;
      text-align: right !important;
    }

    thead {
      th {
        &:nth-child(1) {
          text-align: left !important;
          width: 400px;
        }
        &:nth-child(2) {
          text-align: left !important;
        }

        span {
          color: #333;
          font-weight: 600;
        }

        .v-icon {
          display: none !important;
        }
      }
    }

    tbody {
      td {
        padding-bottom: 5px !important;

        &:nth-child(1) {
          text-align: left !important;
          width: 400px;
        }
        &:nth-child(2) {
          text-align: left !important;
        }

        .negative {
          &:after {
            content: ')';
          }
          &:before {
            content: '(';
          }
        }

        .account {
          .link {
            font-weight: 600;
            width: 100%;

            &.bny {
              color: #02579e;
              cursor: pointer;
            }
          }

          .number {
            color: #02579e;
            margin: 0 0 0 5px;
          }

          .group {
            color: black;
            font-weight: 600;
            font-size: 14px;
          }
        }

        .date {
          font-size: 12px;
        }
      }
    }

    .v-card {
      box-shadow: none;
    }
  }

  .footer {
    padding: 15px 20px 10px;

    &.statements {
      padding: 15px 0 0;

      .v-expansion-panel__container {
        margin: 0;
      }
    }

    .label {
      padding: 0 5px 0 0;
      font-weight: 600;
      padding-left: 42px;

      @media only screen and (min-width: 640px) {
        text-align: right;
      }
    }

    .value {
      text-align: left;

      @media only screen and (min-width: 640px) {
        padding-left: 20px;
      }
    }
  }

  .subfooter {
    padding: 0 20px 0;
  }

  .representative {
    padding: 7px 0 0;

    .title {
      padding: 0;
    }
  }
}
.link-blue-no-dec {
  color: rgb(2, 87, 158);
  font-size: 14px;
  text-decoration: none;
  font-weight: 600;
  cursor: pointer;
}

.v-slide-group__content {
  background: #ffffff;
}
</style>
